import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { storeService, mockStores } from '../services/storeService';
import { Store } from '../types';
import { 
  getSelectedStoreFromStorage, 
  saveSelectedStoreToStorage, 
  trackStoreAccess,
  getStorePreferences,
  isStoreFavorite,
  addStoreToFavorites,
  removeStoreFromFavorites
} from './storeUtils';

interface StoreContextType {
  selectedStore: string;
  currentStore: Store | undefined;
  stores: Store[];
  loading: boolean;
  error: string | null;
  setSelectedStore: (storeId: string) => void;
  refreshStores: () => Promise<void>;
  isStoreFavorite: (storeId: string) => boolean;
  addToFavorites: (storeId: string) => void;
  removeFromFavorites: (storeId: string) => void;
  getStoreDisplayName: (storeId: string) => string;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

const SELECTED_STORE_KEY = 'beautello_selected_store';
const DEFAULT_STORE_ID = '59650b2a-3cd2-463f-b9cb-064c66f486f7'; // Skin Culture store UUID

export const StoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize selectedStore from localStorage or use default
  const [selectedStore, setSelectedStore] = useState(() => {
    const savedStore = getSelectedStoreFromStorage();
    return savedStore || DEFAULT_STORE_ID;
  });
  
  const [stores, setStores] = useState<Store[]>(mockStores); // Start with mock data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const currentStore = stores.find(store => store.id === selectedStore);

  const fetchStores = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await storeService.getStores();
      
      if (fetchError) {
        console.warn('Using mock data due to Supabase error:', fetchError);
        setStores(mockStores); // Fallback to mock data
        setError('Using offline data');
      } else if (data && data.length > 0) {
        setStores(data);
        // If current selected store doesn't exist in fetched data, select the first one
        if (!data.find(store => store.id === selectedStore)) {
          const newStoreId = data[0].id;
          setSelectedStore(newStoreId);
          // Save to localStorage immediately
          saveSelectedStoreToStorage(newStoreId);
        }
      } else {
        setStores(mockStores); // Fallback if no data
        setError('No stores found, using default data');
      }
    } catch (err) {
      console.warn('Error fetching stores, using mock data:', err);
      setStores(mockStores);
      setError('Connection error, using offline data');
    } finally {
      setLoading(false);
    }
  };

  const refreshStores = async () => {
    await fetchStores();
  };

  // Fetch stores on mount
  useEffect(() => {
    fetchStores();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enhanced setSelectedStore function with localStorage persistence and tracking
  const handleSetSelectedStore = (storeId: string) => {
    const store = stores.find(s => s.id === storeId);
    if (!store) {
      console.warn(`Store with ID ${storeId} not found`);
      return;
    }

    console.log(`🏪 Changing store from ${selectedStore} to ${storeId} (${store.name})`);
    setSelectedStore(storeId);
    saveSelectedStoreToStorage(storeId);
    trackStoreAccess(storeId, store.name);
    
    console.log(`✅ Store selection saved: ${store.name}`);
  };

  // Utility functions for favorites
  const handleAddToFavorites = (storeId: string) => {
    addStoreToFavorites(storeId);
    console.log(`⭐ Added store to favorites: ${stores.find(s => s.id === storeId)?.name}`);
  };

  const handleRemoveFromFavorites = (storeId: string) => {
    removeStoreFromFavorites(storeId);
    console.log(`🗑️ Removed store from favorites: ${stores.find(s => s.id === storeId)?.name}`);
  };

  const getStoreDisplayName = (storeId: string): string => {
    const store = stores.find(s => s.id === storeId);
    return store?.name || 'Unknown Store';
  };

  // Validate and potentially correct the selected store when stores change
  useEffect(() => {
    if (stores.length > 0) {
      const storeExists = stores.find(store => store.id === selectedStore);
      if (!storeExists) {
        // If selected store doesn't exist in the current stores list, 
        // select the first available store
        const firstStore = stores[0];
        console.warn(`Selected store ${selectedStore} not found, switching to ${firstStore.name}`);
        handleSetSelectedStore(firstStore.id);
      }
    }
  }, [stores, selectedStore]);

  return (
    <StoreContext.Provider value={{
      selectedStore,
      currentStore,
      stores,
      loading,
      error,
      setSelectedStore: handleSetSelectedStore,
      refreshStores,
      isStoreFavorite,
      addToFavorites: handleAddToFavorites,
      removeFromFavorites: handleRemoveFromFavorites,
      getStoreDisplayName
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};